import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { AuthUser } from '@/types';
import { getMe } from '@/api/me';
import { clearAllFormDrafts } from '@/components/common/formDraft/formDraftStore';
import { unsubscribeMyPush } from '@/api/push/myPush';
import { forgetThisDevice } from '@/pwa/pushClient';

interface AppContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  setSession: (token: string, user: AuthUser) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  /** An external production customer rather than somebody who works here. */
  isPortalUser: boolean;
  /** Where "home" is for this identity — `/portal` for a customer, `/` for everyone else. */
  homePath: string;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const AppContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(!!localStorage.getItem('token'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    getMe()
      .then(setUser)
      .catch(() => {
        localStorage.removeItem('token');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const setSession = (token: string, nextUser: AuthUser) => {
    localStorage.setItem('token', token);
    setUser(nextUser);
  };

  const logout = () => {
    const token = localStorage.getItem('token');
    // A shared phone must stop getting this person's pushes (PWA-PUSH.md §8). Not awaited.
    if (token) void forgetThisDevice(endpoint => unsubscribeMyPush(endpoint, token));
    localStorage.removeItem('token');
    clearAllFormDrafts();
    setUser(null);
  };

  const hasPermission = (permission: string) =>
    !!user && (user.isSuperAdmin || user.permissions.includes('*') || user.permissions.includes(permission));

  /**
   * A portal identity is one whose role holds `portal:use` — the key the migration grants to
   * "לקוח חיצוני" and to nothing else.
   *
   * Super-admins are excluded explicitly. They match every permission check by design, and without
   * this they would be routed into the customer shell and out of their own dashboard.
   */
  const isPortalUser = !!user && !user.isSuperAdmin && user.permissions.includes('portal:use');

  const value = useMemo<AppContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      loading,
      setSession,
      logout,
      hasPermission,
      isPortalUser,
      homePath: isPortalUser ? '/portal' : '/'
    }),
    [user, loading]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextValue => {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error('useAppContext must be used within AppContextProvider');
  }
  return ctx;
};
